import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePitchDashboardData } from '@/hooks/usePitchDashboardData';
import FeedbackReportModal from '@/components/Cards/FeedbackReportModal';
import { useDashboardTheme } from './themeHelper';

// ─── Types ─────────────────────────────────────────────────────────────────

type SlimPitch = {
  id: string;
  salesRep: string;
  userId: string;
  company: string;
  industry: string;
  status: string;
  overallScore: number;
  createdAt: string;
  pitchType: string;
  isScenario: boolean;
  pitchTitle: string;
  meetingPurpose: string[];
  meetingObjective: string[];
  summary: string[];
  insights: string[];
  suggestions: string[];
  justification: string[];
  categories: any[];
  ai_score: any;
  ai_summary: any;
  talkRatio: { agent: number; customer: number };
  // For FeedbackReportModal compatibility
  salesRepRole: 'admin' | 'manager' | 'user';
  customer: string;
  businessModel: string;
  decisionProcess: string;
  techStack: string;
  durationMinutes: number;
  isRecorded: boolean;
  audioUrl: string;
  aiData: {
    summary: string[];
    insights: string[];
    suggestions: string[];
    justification: string[];
    categories: any[];
  };
};

// ─── Config ────────────────────────────────────────────────────────────────

const typeConfig: Record<string, { label: string; color: string }> = {
  call:        { label: 'Calls',            color: '#38bdf8' },
  demo:        { label: 'Demos',            color: '#a78bfa' },
  negotiation: { label: 'Negotiations',     color: '#f59e0b' },
  objection:   { label: 'Objections',       color: '#f87171' },
  scenario:    { label: 'Practice Scenarios', color: '#2dd4bf' },
};

const statusConfig: Record<string, { dot: string; color: string }> = {
  Active:     { dot: '#34d399', color: '#34d399' },
  Processing: { dot: '#38bdf8', color: '#38bdf8' },
  Completed:  { dot: '#a78bfa', color: '#a78bfa' },
  Stalled:    { dot: '#f87171', color: '#f87171' },
};

// ─── Icons ─────────────────────────────────────────────────────────────────

const IconCall = () => (
  <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5.75A2.75 2.75 0 015.75 3h.938a1 1 0 01.97.757l.918 3.674a1 1 0 01-.48 1.118L6.3 9.524a11.047 11.047 0 005.176 5.176l1.975-1.796a1 1 0 011.118-.48l3.674.918A1 1 0 0119 14.312v.938A2.75 2.75 0 0116.25 18C9.484 18 3 11.516 3 4.75v-1z" />
  </svg>
);
const IconDemo = () => (
  <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25A2.25 2.25 0 015.25 3h13.5A2.25 2.25 0 0121 5.25z" />
  </svg>
);
const IconNego = () => (
  <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
  </svg>
);
const IconObj = () => (
  <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
  </svg>
);
const IconScenario = () => (
  <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" />
  </svg>
);

const TypeIcon = ({ type }: { type: string }) => {
  switch (type) {
    case 'demo':        return <IconDemo />;
    case 'negotiation': return <IconNego />;
    case 'objection':   return <IconObj />;
    case 'scenario':    return <IconScenario />;
    default:            return <IconCall />;
  }
};

// ─── FIELD_MAX for AI score ─────────────────────────────────────────────────

const FIELD_MAX: Record<string, number> = {
  captured_attention: 4, stated_purpose: 3, established_relevance: 3,
  asked_relevant_questions: 3, listened_actively: 3, identified_needs: 3,
  confirmed_understanding: 3, communicated_value: 3, structured_pitch: 3,
  used_clear_language: 3, kept_customer_engaged: 3, handled_objections: 3,
  asked_for_feedback: 3, sounded_confident: 4, used_positive_language: 3,
  maintained_energy: 3, summarized_key_points: 3, asked_for_next_step: 3,
  created_urgency: 3, professionalism: 4, rapport_building: 3,
  alignment_with_customer: 3, adaptability: 3, meeting_objective_achieved: 3,
};

const buildCategories = (ai_score: any) => {
  if (!ai_score) return [];
  const sections = [
    { key: 'opening',    label: 'Opening',            color: '#a78bfa', src: ai_score.opening },
    { key: 'discovery',  label: 'Discovery',          color: '#38bdf8', src: ai_score.discovery },
    { key: 'pitch',      label: 'Pitch Delivery',     color: '#34d399', src: ai_score.pitch_delivery },
    { key: 'engagement', label: 'Engagement',         color: '#f59e0b', src: ai_score.engagement },
    { key: 'confidence', label: 'Confidence & Tone',  color: '#fb923c', src: ai_score.confidence_and_tone },
    { key: 'closing',    label: 'Closing',            color: '#f472b6', src: ai_score.closing },
    { key: 'impression', label: 'Overall Impression', color: '#60a5fa', src: ai_score.overall_impression },
  ];
  return sections
    .filter((s) => s.src && typeof s.src === 'object')
    .map((s) => ({
      key: s.key, label: s.label, color: s.color,
      metrics: Object.entries(s.src)
        .filter(([, v]) => typeof v === 'number')
        .map(([k, v]) => ({
          label: k.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
          score: Number(v), max: (FIELD_MAX[k] ?? 3),
        })),
    }))
    .filter((c) => c.metrics.length > 0);
};

// ─── Transform raw pitch → SlimPitch ───────────────────────────────────────

const transformPitch = (raw: any): SlimPitch => {
  const isScenario = raw.isScenario === true || raw.meetingType === 'scenario';
  const aiScore    = raw.ai_score ?? raw.overallScore ?? null;
  const normalizedStatus = raw.status
    ? raw.status.charAt(0).toUpperCase() + raw.status.slice(1).toLowerCase()
    : 'Active';

  const summaryArr     = raw.ai_summary?.summary     ?? raw.summary     ?? [];
  const insightsArr    = raw.ai_summary?.insights    ?? raw.insights    ?? [];
  const suggestionsArr = raw.ai_summary?.suggestions ?? raw.suggestions ?? [];

  return {
    id:           raw.id || '',
    salesRep:     raw.User?.name || raw.user?.name || 'Unknown',
    userId: raw.User?.id || raw.user?.id || raw.userId || raw.createdBy || '',

    company:      raw.TargetCompanyProfile?.companyName || raw.companyName || '',
    industry: raw.TargetCompanyProfile?.industry || raw.companyName || '',
    status:       normalizedStatus,
    overallScore: Math.min(100, raw.overallPercentage ?? raw.score ?? 0),
    createdAt:    raw.createdAt || '',
    pitchType:    raw.pitchType || raw.meetingType || 'call',
    isScenario,
    pitchTitle: raw.pitchTitle || '',    meetingPurpose:   raw.otherDetails?.meetingPurpose   ?? [],
    meetingObjective: raw.otherDetails?.meetingObjective ?? [],
    summary:     Array.isArray(raw.summary)     ? raw.summary     : 
                 raw.ai_summary?.summary         ? [raw.ai_summary.summary] : [],
    insights:    Array.isArray(raw.insights)    ? raw.insights    : 
                 raw.ai_summary?.insights        ?? [],
    suggestions: Array.isArray(raw.suggestions) ? raw.suggestions : 
                 raw.ai_summary?.suggestions     ?? [],
  
    justification: raw.categoryScores?.justification ?? [],
    categories:    buildCategories(aiScore),
    ai_score:      aiScore,
    ai_summary:    raw.ai_summary ?? {},
    talkRatio: { agent: raw.talkRatio?.agent || 60, customer: raw.talkRatio?.customer || 40 },
    // FeedbackReportModal compatibility
    salesRepRole:   (raw.User?.role || 'user') as 'admin' | 'manager' | 'user',
    customer:       '',
    businessModel:  '',
    decisionProcess: '',
    techStack:      '',
    durationMinutes: 0,
    isRecorded:     false,
    audioUrl:       '',
    aiData: {
      summary:       summaryArr,
      insights:      insightsArr,
      suggestions:   suggestionsArr,
      justification: raw.categoryScores?.justification ?? [],
      categories:    buildCategories(aiScore),
    },
  };
};

// ─── Shimmer ───────────────────────────────────────────────────────────────

const ShimmerSlim = () => (
  <div className="rounded-xl border border-white/[0.05] bg-[#1A1726] px-4 py-3.5 flex items-center gap-3 animate-pulse">
    <div className="w-9 h-9 rounded-lg bg-white/[0.07] flex-shrink-0" />
    <div className="flex-1 space-y-2">
      <div className="h-3 w-40 rounded-full bg-white/[0.07]" />
      <div className="h-2 w-28 rounded-full bg-white/[0.05]" />
    </div>
    <div className="h-8 w-28 rounded-lg bg-white/[0.07]" />
  </div>
);

// ─── Slim Pitch Card ────────────────────────────────────────────────────────

const SlimPitchCard: React.FC<{
  pitch: SlimPitch;
  color: string;
  onFeedback: (p: SlimPitch) => void;
}> = ({ pitch, color, onFeedback }) => {
  const scoreColor = pitch.overallScore >= 90 ? '#4ADE80' : pitch.overallScore >= 70 ? '#A3E635' : pitch.overallScore >= 50 ? '#FACC15' : '#F87171';
  const stCfg = statusConfig[pitch.status] || statusConfig['Active'];
  const circ = 2 * Math.PI * 16;

  return (
    <div className="group flex items-center gap-3 rounded-xl border border-white/[0.06] bg-[#1A1726] px-4 py-3.5 transition-all duration-200 hover:border-white/[0.1] hover:bg-[#1D1A2E]">
      {/* Score ring */}
      <div className="relative flex-shrink-0 w-10 h-10">
        <svg width="40" height="40" viewBox="0 0 40 40">
          <circle cx="20" cy="20" r="16" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="2.5" />
          <circle cx="20" cy="20" r="16" fill="none" stroke={scoreColor} strokeWidth="2.5"
            strokeDasharray={`${(pitch.overallScore / 100) * circ} ${circ}`}
            strokeLinecap="round" transform="rotate(-90 20 20)" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[11px] font-bold text-white">{pitch.overallScore}</span>
        </div>
      </div>

      {/* Info */}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[14px] font-semibold text-white truncate">{pitch.salesRep}</span>
          <span
            className="flex items-center gap-1 rounded-full px-1.5 py-[2px] text-[10px] font-medium"
            style={{ background: `${stCfg.color}12`, color: stCfg.color, border: `1px solid ${stCfg.color}25` }}
          >
            <span className="w-1 h-1 rounded-full" style={{ background: stCfg.dot }} />
            {pitch.status}
          </span>
          {pitch.isScenario && (
            <span className="rounded-full px-1.5 py-[2px] text-[10px] font-medium bg-[#2dd4bf]/10 text-[#2dd4bf] border border-[#2dd4bf]/20">
              Scenario
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
          {pitch.pitchTitle && (
            <span className="text-[13px] font-medium text-gray-300 truncate max-w-[260px]">{pitch.pitchTitle}</span>
          )}
          {pitch.company && (
            <>
              {pitch.pitchTitle && <span className="text-gray-700 text-xs">·</span>}
              <span className="text-[13px] text-gray-400 truncate">{pitch.company}</span>
            </>
          )}
          {pitch.industry && (
            <>
              <span className="text-gray-700 text-xs">·</span>
              <span className="text-[12px] text-gray-500">{pitch.industry}</span>
            </>
          )}
          {pitch.createdAt && (
            <>
              <span className="text-gray-700 text-xs">·</span>
              <span className="text-[12px] text-gray-600">
                {new Date(pitch.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </>
          )}
        </div>

        {pitch.meetingPurpose.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {pitch.meetingPurpose.slice(0, 3).map((p, i) => (
              <span
                key={i}
                className="text-[10px] rounded-full px-1.5 py-px font-medium"
                style={{ background: `${color}10`, color: `${color}bb`, border: `1px solid ${color}20` }}
              >
                {p.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        )}

        {pitch.summary.length > 0 && (
          <p className="text-[12px] text-gray-500 mt-1 line-clamp-1">{pitch.summary[0]}</p>
        )}
      </div>

      {/* View Feedback */}
      <div className="flex-shrink-0">
        <button
          onClick={() => onFeedback(pitch)}
          className="flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-[13px] font-semibold transition-all hover:opacity-80 border"
          style={{ background: `${color}12`, color, borderColor: `${color}28` }}
        >
          <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className="hidden sm:inline">View Feedback</span>
        </button>
      </div>
    </div>
  );
};

const ScenarioCard: React.FC<{
  pitch: SlimPitch;
  onFeedback: (p: SlimPitch) => void;
}> = ({ pitch, onFeedback }) => {
  const color = '#2dd4bf';
  const scoreColor = pitch.overallScore >= 90 ? '#4ADE80' : pitch.overallScore >= 70 ? '#A3E635' : pitch.overallScore >= 50 ? '#FACC15' : '#F87171';
  const circ = 2 * Math.PI * 16;

  return (
    <div className="group flex items-center gap-3 rounded-xl border border-[#2dd4bf]/15 bg-[#1A1726] px-4 py-3.5 transition-all duration-200 hover:border-[#2dd4bf]/30 hover:bg-[#1D1A2E]">
      {/* Score ring */}
      <div className="relative flex-shrink-0 w-10 h-10">
        <svg width="40" height="40" viewBox="0 0 40 40">
          <circle cx="20" cy="20" r="16" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="2.5" />
          <circle cx="20" cy="20" r="16" fill="none" stroke={scoreColor} strokeWidth="2.5"
            strokeDasharray={`${(pitch.overallScore / 100) * circ} ${circ}`}
            strokeLinecap="round" transform="rotate(-90 20 20)" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-[11px] font-bold text-white">{pitch.overallScore}</span>
        </div>
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[14px] font-semibold text-white truncate">{pitch.salesRep}</span>
          <span className="rounded-full px-1.5 py-[2px] text-[10px] font-medium bg-[#2dd4bf]/10 text-[#2dd4bf] border border-[#2dd4bf]/20">
            Scenario
          </span>
          <span className="rounded-full px-1.5 py-[2px] text-[10px] font-medium"
            style={{ background: 'rgba(167,139,250,0.1)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.2)' }}>
            {pitch.status}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 mt-0.5">
          {pitch.pitchTitle && (
            <span className="text-[13px] font-medium text-[#2dd4bf]/80 truncate max-w-[260px]">
              {pitch.pitchTitle}
            </span>
          )}
          {pitch.industry && (
            <>
              <span className="text-gray-700 text-xs">·</span>
              <span className="text-[12px] text-gray-500">{pitch.industry}</span>
            </>
          )}
          {pitch.createdAt && (
            <>
              <span className="text-gray-700 text-xs">·</span>
              <span className="text-[12px] text-gray-600">
                {new Date(pitch.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            </>
          )}
        </div>

        {/* Summary preview */}
        {pitch.summary.length > 0 && (
          <p className="text-[12px] text-gray-500 mt-1 line-clamp-1">{pitch.summary[0]}</p>
        )}
      </div>

      <div className="flex-shrink-0">
        <button
          onClick={() => onFeedback(pitch)}
          className="flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-[13px] font-semibold transition-all hover:opacity-80 border"
          style={{ background: `${color}12`, color, borderColor: `${color}28` }}
        >
          <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className="hidden sm:inline">View Feedback</span>
        </button>
      </div>
    </div>
  );
};

// ─── Page ──────────────────────────────────────────────────────────────────

const PitchTypeListPage: React.FC = () => {
  const { isDark, colors } = useDashboardTheme();
  const { userId, type } = useParams<{ userId: string; type: string }>();
  const navigate = useNavigate();

  const pitchType      = (type || 'call').toLowerCase();
  const isScenarioPage = pitchType === 'scenario';
  const config         = typeConfig[pitchType] || typeConfig.call;

  const [searchQuery,   setSearchQuery]   = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [selectedFeedback, setSelectedFeedback] = useState<SlimPitch | null>(null);

  // Fetch all pitches via existing hook (enabled=true always on this page)
  const { rawPitches, isLoading, isError, refetchAll } = usePitchDashboardData(true);

  // Transform all raw pitches
  const allPitches = useMemo(() => rawPitches.map(transformPitch), [rawPitches]);

  // Filter by userId + pitchType
  const typedPitches = useMemo(() => {
    return allPitches.filter((p) => {
      if (userId && p.userId !== userId) return false;
      if (isScenarioPage) return p.isScenario;
      return !p.isScenario && p.pitchType === pitchType;
    });
  }, [allPitches, userId, pitchType, isScenarioPage]);

  // Company options
  const companyOptions = useMemo(() => {
    return [...new Set(typedPitches.map((p) => p.company).filter(Boolean))].sort();
  }, [typedPitches]);

  // Final filtered list
  const filtered = useMemo(() => {
    return typedPitches.filter((p) => {
      if (companyFilter && p.company !== companyFilter) return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (
          !p.salesRep.toLowerCase().includes(q) &&
          !p.company.toLowerCase().includes(q) &&
          !(p.pitchTitle || '').toLowerCase().includes(q) &&
          !p.industry.toLowerCase().includes(q)
        ) return false;
      }
      return true;
    });
  }, [typedPitches, companyFilter, searchQuery]);

  return (
    <main className={"flex-1 p-4 md:p-6 mt-15 min-h-screen " + (isDark ? "" : "light-theme-dashboard")} style={{ background: isDark ? '#13111C' : colors.bg }}>

      {/* ── Header ── */}
      <div className="flex items-center gap-3 mb-5">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 rounded-xl border border-white/[0.07] bg-white/[0.03] px-3 py-2 text-[13px] text-gray-500 hover:text-gray-200 hover:bg-white/[0.05] transition-colors"
        >
          <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M11 17l-5-5m0 0l5-5m-5 5h12" />
          </svg>
          Back
        </button>

        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center border"
            style={{ background: `${config.color}10`, borderColor: `${config.color}25`, color: config.color }}
          >
            <TypeIcon type={pitchType} />
          </div>
          <div>
            <h1 className="text-[18px] font-bold text-white tracking-tight leading-none">{config.label}</h1>
            <p className="text-[12px] text-gray-600 mt-0.5">
              {isLoading ? '—' : `${filtered.length} pitch${filtered.length !== 1 ? 'es' : ''}`}
              {userId ? ' · this rep' : ''}
            </p>
          </div>
        </div>
      </div>

      {/* ── Error ── */}
      {isError && (
        <div className="mb-4 flex items-center gap-2.5 p-3 bg-rose-500/8 border border-rose-500/18 rounded-xl text-rose-400 text-[13px]">
          <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M12 3a9 9 0 100 18A9 9 0 0012 3z" />
          </svg>
          Failed to load pitches.
          <button
            onClick={refetchAll}
            className="ml-auto text-[11px] border border-rose-500/25 rounded-full px-2.5 py-1 hover:bg-rose-500/10"
          >
            Retry
          </button>
        </div>
      )}

      {/* ── Filters ── */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[160px]">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
            width="13" height="13" fill="none" viewBox="0 0 24 24"
            stroke="rgba(255,255,255,0.2)" strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />
          </svg>
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={`Search ${config.label.toLowerCase()}…`}
            className="w-full rounded-lg bg-[#1E1B2E] border border-white/[0.07] text-[14px] text-white placeholder-gray-700 pl-8 pr-3 py-2.5 outline-none focus:border-white/[0.18] transition-colors"
          />
        </div>

        {companyOptions.length > 0 && (
          <select
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
            className="rounded-lg bg-[#1E1B2E] border border-white/[0.07] text-[14px] text-gray-400 px-3 py-2.5 outline-none cursor-pointer"
            style={{ minWidth: 150 }}
          >
            <option value="">All Companies</option>
            {companyOptions.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}

        {(searchQuery || companyFilter) && (
          <button
            onClick={() => { setSearchQuery(''); setCompanyFilter(''); }}
            className="rounded-lg bg-white/[0.03] border border-white/[0.07] text-[13px] text-gray-600 px-3 py-2.5 hover:text-gray-300 transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      {/* ── Cards ── */}
      <div className="grid gap-2">
        {isLoading
          ? Array.from({ length: 5 }).map((_, i) => <ShimmerSlim key={i} />)
          : !filtered.length
            ? (
              <div className="flex flex-col items-center justify-center h-44 rounded-2xl border border-dashed border-white/[0.05] bg-[#1A1726] gap-2.5">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center border opacity-30"
                  style={{ background: `${config.color}10`, borderColor: `${config.color}25`, color: config.color }}
                >
                  <TypeIcon type={pitchType} />
                </div>
                <p className="text-gray-600 text-[14px]">No {config.label.toLowerCase()} found</p>
                {(searchQuery || companyFilter) && (
                  <button
                    onClick={() => { setSearchQuery(''); setCompanyFilter(''); }}
                    className="text-[12px] text-gray-600 hover:text-gray-400 underline underline-offset-2 transition"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            )
         : filtered.map((pitch) => (
  isScenarioPage
    ? <ScenarioCard key={pitch.id} pitch={pitch} onFeedback={setSelectedFeedback} />
    : <SlimPitchCard key={pitch.id} pitch={pitch} color={config.color} onFeedback={setSelectedFeedback} />
            ))
        }
      </div>

      {/* ── Feedback Modal ── */}
      {selectedFeedback && (
        <FeedbackReportModal
          open={!!selectedFeedback}
          onClose={() => setSelectedFeedback(null)}
          meeting={selectedFeedback as any}
        />
      )}
    </main>
  );
};

export default PitchTypeListPage;